import axios, { AxiosError } from 'axios';
import { createContext, useContext } from 'react';

const isLocalDev = window.location.hostname === 'localhost';

export const uri = isLocalDev
  ? `${window.location.protocol}//${window.location.hostname}:3000`
  : window.location.origin;

export interface AuthUser {
  sub: string;
  username: string;
  isGuest?: boolean;
}

function parseJwt(token: string): AuthUser | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

// How long before a token's actual expiry to treat it as already stale —
// covers the gap between "still technically valid" and "will have expired
// by the time it reaches the server", including the round trip itself.
const TOKEN_EXPIRY_BUFFER_MS = 60_000;

function isExpiringSoon(token: string): boolean {
  const payload = parseJwt(token) as { exp?: number } | null;
  if (!payload?.exp) return true;
  return payload.exp * 1000 - Date.now() < TOKEN_EXPIRY_BUFFER_MS;
}

export const AuthContext = createContext<AuthUser | null>(null);

export const useAuth = (): AuthUser | null => useContext(AuthContext);

export const getToken = () => accessToken;

// Used for connections that outlive a single request (the game websocket):
// returns the cached token if it's still comfortably valid, otherwise
// refreshes first — so a caller who's about to hand this token to something
// that won't get another chance to react to a 401 (like a socket.io `auth`
// callback on a reconnection attempt) always gets a live one.
export const getFreshToken = async (): Promise<string | null> => {
  if (accessToken && !isExpiringSoon(accessToken)) return accessToken;
  return doRefresh();
};

export const isLoggedIn = () => accessToken !== null;

export const getCurrentUser = (): AuthUser | null =>
  accessToken ? parseJwt(accessToken) : null;

const onTokenChangeListeners: Array<(user: AuthUser | null) => void> = [];
export const addTokenChangeListener = (
  callback: (user: AuthUser | null) => void
) => onTokenChangeListeners.push(callback);
export const removeTokenChangeListener = (
  callback: (user: AuthUser | null) => void
) => {
  const index = onTokenChangeListeners.indexOf(callback);
  if (index === -1) {
    return;
  }
  onTokenChangeListeners.splice(index, 1);
};

function notifyListeners() {
  const user = getCurrentUser();
  onTokenChangeListeners.forEach((cb) => cb(user));
}

const api = axios.create({
  baseURL: uri,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !(originalRequest as any)._retry
    ) {
      (originalRequest as any)._retry = true;
      const newToken = await doRefresh();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

async function doRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await api.post('/auth/refresh');
        const token = response.data?.accessToken;
        accessToken = token;
        notifyListeners();
        return token;
      } catch {
        accessToken = null;
        notifyListeners();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export function setAccessToken(token: string) {
  accessToken = token;
  notifyListeners();
}

export async function initAuth(): Promise<AuthUser | null> {
  const token = await doRefresh();
  return token ? parseJwt(token) : null;
}

export const login = async (
  username: string,
  password: string
): Promise<boolean> => {
  try {
    const response = await api.post('/auth/login', { username, password });
    accessToken = response.data?.accessToken;
    notifyListeners();
    return true;
  } catch {
    return false;
  }
};

export const registerUser = async (
  username: string,
  email: string,
  password: string
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
    });
    accessToken = response.data?.accessToken;
    notifyListeners();
    return { ok: true };
  } catch (e) {
    const axiosErr = e as AxiosError<{
      message?: string | string[];
      error?: string;
    }>;
    const msg = axiosErr.response?.data?.message;
    const message = Array.isArray(msg) ? msg[0] : msg || 'Registration failed.';
    return { ok: false, error: message };
  }
};

export const startGuestSession = async (): Promise<boolean> => {
  try {
    const response = await api.post('/auth/guest');
    accessToken = response.data?.accessToken;
    notifyListeners();
    return true;
  } catch {
    return false;
  }
};

export const upgradeGuest = async (
  username: string,
  email: string,
  password: string
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const response = await api.post('/auth/guest/upgrade', {
      username,
      email,
      password,
    });
    accessToken = response.data?.accessToken;
    notifyListeners();
    return { ok: true };
  } catch (e) {
    const axiosErr = e as AxiosError<{
      message?: string | string[];
      error?: string;
    }>;
    const msg = axiosErr.response?.data?.message;
    const message = Array.isArray(msg) ? msg[0] : msg || 'Upgrade failed.';
    return { ok: false, error: message };
  }
};

export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch {
  } finally {
    accessToken = null;
    notifyListeners();
  }
};

export class ApiError extends Error {
  response?: AxiosError['response'];
}

async function request<T>(
  method: 'get' | 'post' | 'put' | 'delete',
  endpoint: string,
  body?: any
): Promise<T> {
  try {
    const config =
      method === 'get'
        ? [endpoint]
        : method === 'delete'
        ? [endpoint, body !== undefined ? { data: body } : undefined]
        : [endpoint, body];
    const response = await (api as any)[method](...config);
    return response.data;
  } catch (e) {
    const err = new ApiError(
      `Request failed: ${method.toUpperCase()} ${endpoint}`
    );
    err.response = (e as AxiosError).response;
    throw err;
  }
}

export const get = async <T = any>(endpoint: string): Promise<T> =>
  request<T>('get', endpoint);

export const post = async <T = any>(endpoint: string, body: any): Promise<T> =>
  request<T>('post', endpoint, body);

export const put = async <T = any>(endpoint: string, body: any): Promise<T> =>
  request<T>('put', endpoint, body);

export const del = async <T = any>(endpoint: string, body?: any): Promise<T> =>
  request<T>('delete', endpoint, body);
